import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  Tabs,
  Tab,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Card,
  Modal,
  TextField,
  InputAdornment,
  Alert,
  FormControlLabel,
  Switch,
  Tooltip,
  Dialog,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Link,
} from '@mui/material';
import dayjs from 'dayjs';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ScienceIcon from '@mui/icons-material/Science';
import api from '../../utils/functions/api';
import { EXP_STATUS_MAP } from '../../utils/constants/financeStatus.constant';
import ProjectEditPopup from './ProjectEditPopup';
import _ from 'lodash';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import OpportunityDetail from '../Marketing/OpportunityDetail';
import * as actions from '../../redux/actions';
import { DevWinBadge, DevTabBadge } from '../../components/DevPageBadge';
import { BudgetForm, DeliverForm } from '../../components/Forms';
import { CSVUploadModal } from '../../components/Modal';
import BudgetTable from '../../components/Table/BudgetTable';
import BudgetListTable from '../../components/Table/BudgetListTable';
import { TruncatedTooltipText } from './projectSingleDashboard/components/TruncatedTooltipText';
import { ProjectHistoryDialog } from './projectSingleDashboard/components/ProjectHistoryDialog';
import { ProjectSandboxDialog } from './projectSingleDashboard/components/ProjectSandboxDialog';
import CheckInlist from '../../components/Table/CheckInlist';
import { ExpensesTableProject } from '../../components/Table';
import ExpensesDetail from '../Finance/ExpensesDetail';
import PendingItemsModal from '../../components/Modal/PendingItemsModal';
import RefundItemsModal from '../../components/Modal/RefundItemsModal';
import ProjectSingleDashboard from './ProjectSingleDashboard';
import PopupDragContext from './PopupDragContext';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { projectExpensesReport } from '../../components/PDF';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { useLang } from '../../components/Table/projectDashboard/LangContext';

const fmt = (n) =>
  n != null
    ? `${Number(n)
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
    : '0.00';
const fmtPct = (num, den) =>
  den ? `${((num / den) * 100).toFixed(1)}%` : null;

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1000,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export default function ProjectPopup({
  propId,
  onClose,
  onSaved,
  onPopOut,
  onPopIn,
  onPrev,
  onNext,
  navIndex,
  navTotal,
  filterChips,
  initialData,
  sheet: sheetProp, // optional — ถ้า parent ส่งมา (เช่น ProjectList.js/ProjectPopupWindow.js) จะ
  // ใช้ค่านี้แทน state ภายใน เพื่อให้แท็บ (แดชบอร์ด/รายละเอียด) ไม่รีเซ็ตตอนกดเลื่อนก่อนหน้า/ถัดไป
  // (ซึ่งทำให้ component นี้ remount ใหม่ทั้งก้อนจาก key={selectedId}/key={id} ที่ parent ตั้งไว้)
  onSheetChange, // คู่กับ sheetProp — เรียกตอนสลับแท็บ ถ้าไม่ส่งมาจะ fallback ใช้ state ภายในเหมือนเดิม
}) {
  const dispatch = useDispatch();
  const { lang } = useLang();
  const [popupLang, setPopupLang] = useState('en');
  const id = propId;
  // มีค่าเฉพาะตอนอยู่ใน modal ที่ลากย้ายตำแหน่งได้ (ครอบด้วย DraggablePaper ใน ProjectList.js)
  const recenterPopup = useContext(PopupDragContext);

  const project = useSelector((state) => state.project);
  const budget = useSelector((state) => state.budget);
  const timestamp = useSelector((state) => state.timestamp);
  const info = useSelector((state) => state.info);
  const me = useSelector((state) => state.me);

  const [cachedProject, setCachedProject] = useState(initialData || null);
  const [showOpportunity, setShowOpportunity] = useState(false);
  const [internalSheet, setInternalSheet] = useState(0); // 0 = แดชบอร์ดโปรเจกต์ (ค่าเริ่มต้น), 1 = รายละเอียด P111 เดิมทั้งหมด
  const sheet = sheetProp !== undefined ? sheetProp : internalSheet;
  const setSheet = onSheetChange || setInternalSheet;
  const [tab, setTab] = useState(0);
  const L = (th, en) => (popupLang === 'en' ? en : th);

  // ปุ่มภาษาหลัก P-5 บังคับ Popup T-1 ถึง T-7 ทุกครั้งที่เปลี่ยน
  // ปุ่มใน Popup เปลี่ยนทั้ง T-1 ถึง T-7 พร้อมกัน แต่ไม่กระทบแผง Filter
  useEffect(() => {
    setPopupLang(lang);
  }, [lang]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const [openEdit, setOpenEdit] = useState(false);
  const [openBudget, setOpenBudget] = useState(false);
  const [nextBudgetNo, setNextBudgetNo] = useState('');
  const [openEditBudget, setOpenEditBudget] = useState(false);
  const [openDeliver, setOpenDeliver] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [openTimeTracking, setOpenTimeTracking] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [dateDeliver, setDateDeliver] = useState(null);
  const [datePayment, setDatePayment] = useState(null);
  const [expenseDialogId, setExpenseDialogId] = useState(null);
  const [refundModalBudgetId, setRefundModalBudgetId] = useState(null);
  const [pendingModalBudgetId, setPendingModalBudgetId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [timeTrackingEnabled, setTimeTrackingEnabled] = useState(false);
  const [timeTrackingLinkEnabled, setTimeTrackingLinkEnabled] = useState(true);
  const [expensesList, setExpensesList] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const budgetTableRef = useRef(null);
  const [budgetToggleState, setBudgetToggleState] = useState({ allExpanded: false, disabled: true });

  const { control, handleSubmit, reset, setValue } = useForm();

  useEffect(() => {
    if (!openBudget) return;
    api
      .get(`${process.env.REACT_APP_API_URL}/budget/next-number`)
      .then(({ data }) => setNextBudgetNo(data?.budget_number || ''))
      .catch(() => setNextBudgetNo(''));
  }, [openBudget]);

  useEffect(() => {
    if (!id) return;
    dispatch(actions.getInformation());
    dispatch(actions.budgetAll({ project_id: id }));
    dispatch(actions.timestampAll({ project_id: id }));
    dispatch(actions.meGet());
    // ดึง project ใหม่ในพื้นหลัง — ถ้ามี initialData แสดงข้อมูลเดิมก่อนได้เลย
    // ใช้ค่าที่ projectGet คืนมาตรงๆ แทนการรอ useEffect ดัก state.project เพราะ state.project
    // เป็น slice เดียวกับที่ projectAll (list) เขียนทับได้ — ถ้า projectAll resolve ทีหลัง
    // projectGet ของ modal นี้ จะเขียนทับข้อมูลโปรเจกต์เดี่ยวหายไปเงียบๆ (ดู proj_dash_cache
    // gotcha ใน CLAUDE.md) ใช้ return value ตรงๆ กัน race นี้ได้ทั้งหมด
    dispatch(actions.projectGet(id)).then((fresh) => {
      if (fresh) setCachedProject(fresh);
    });
  }, [id, openBudget, openEditBudget]);

  // เดิมมี useEffect ดัก state.project (redux) แล้ว setCachedProject ตาม — ลบออกแล้ว เพราะ
  // state.project เป็น slice เดียวกับที่ projectAll (list) ใช้ ถ้า fetch โปรเจกต์ก่อนหน้า
  // (ก่อนกดเลื่อนไปโปรเจกต์นี้) ยัง pending อยู่แล้วมา resolve ทีหลัง จะ dispatch PROJECT_GET
  // ทับ state.project ด้วยข้อมูลโปรเจกต์เก่า (เช่น seed_received ของโปรเจกต์ก่อนหน้า) ทำให้
  // ตัวเลขในหน้านี้เปลี่ยนไปทับของถูกต้องเงียบๆ — ใช้ผลลัพธ์ตรงจาก dispatch(projectGet(id))
  // ด้านบน (ผูกกับ id ของ effect run นั้นๆ ตรงๆ ไม่พึ่ง shared state) เป็นแหล่งความจริงเดียวพอ
  useEffect(() => {
    setTimeTrackingEnabled(project?.time_tracking_enabled || false);
    setTimeTrackingLinkEnabled(project?.time_tracking_link_enabled !== false);
  }, [project]);

  useEffect(() => {
    if (tab !== 2 || !id) return;
    setExpensesLoading(true);
    api
      .get(
        `${process.env.REACT_APP_API_URL}/expenses?findProject=${id}&size=2000`,
      )
      .then((res) => {
        const rows = res.data?.rows || res.data || [];
        setExpensesList(
          [...rows].sort((a, b) => new Date(b.date) - new Date(a.date)),
        );
      })
      .catch(console.error)
      .finally(() => setExpensesLoading(false));
  }, [tab, id]);

  const onSubmitBudget = async (data) => {
    if (!(await confirmAsync('บันทึก'))) return;
    try {
      await dispatch(actions.budgetCreate({ ...data, project_id: id }));
      dispatch(actions.projectGet(id));
      onSaved?.();
      reset();
      setOpenBudget(false);
    } catch {
      showAlert('มีรหัสงบประมาณนี้ในระบบแล้ว');
    }
  };

  const onSubmitEditBudget = async (data) => {
    if (!(await confirmAsync('บันทึก'))) return;
    try {
      await dispatch(actions.budgetPut(data.id, { ...data, project_id: id }));
      dispatch(actions.projectGet(id));
      onSaved?.();
      reset();
      setOpenEditBudget(false);
    } catch {
      showAlert('มีรหัสงบประมาณนี้ในระบบแล้ว');
    }
  };

  const handleEditBudget = (data) => {
    setOpenEditBudget(true);
    setValue('budget_number', data?.budget_number);
    setValue('name', data?.name);
    setValue('cost', data?.cost);
    setValue('id', data?.id);
  };

  const handleDeleteBudget = async (item) => {
    if (!_.isEmpty(item?.expenses))
      return showAlert('ไม่สามารถลบงบประมาณที่มีรายการค่าใช้จ่ายผูกอยู่ได้');
    if (await confirmAsync('ลบงบประมาณนี้?')) {
      await dispatch(actions.budgetDelete(item?.id));
      dispatch(actions.projectGet(id));
      dispatch(actions.budgetAll({ project_id: id }));
      onSaved?.();
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (await confirmAsync('ลบข้อมูล')) {
      await dispatch(actions.expensesDelete(expenseId));
      dispatch(actions.expensesAll({}));
    }
  };

  const handleEditExpense = (expenseId) => setExpenseDialogId(expenseId);

  const handleExportExpensesPdf = () => {
    projectExpensesReport(expensesList, p);
  };

  const handleApprove = async (expenseId) => {
    await dispatch(
      actions.expensesPut(expenseId, {
        status: 'APPROVE',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    dispatch(actions.expensesAll({}));
  };

  const handleSuccess = async (expenseId) => {
    await dispatch(actions.expensesPut(expenseId, { status: 'SUCCESS' }));
    dispatch(actions.expensesAll({}));
  };

  const handleDeliver = async () => {
    if (!dateDeliver) return showAlert('เลือกวันที่ส่งมอบงาน');
    if (await confirmAsync('ยืนยันข้อมูล')) {
      await dispatch(
        actions.projectPut(id, {
          deliver_status: { status: true, date: dateDeliver },
          operation_status: 'DELIVERY',
        }),
      );
      dispatch(actions.projectGet(id));
      onSaved?.();
      setOpenDeliver(false);
    }
  };

  const handleAddFromFile = async () => {
    if (_.isEmpty(csvData)) return showAlert('ไม่สามารถอ่านไฟล์รายการได้');
    try {
      await dispatch(actions.budgetCreate({ arr: csvData, project_id: id }));
      showAlert('สำเร็จ');
      setCsvUploadOpen(false);
      setCsvData([]);
      dispatch(actions.budgetAll({ project_id: id }));
      onSaved?.();
    } catch {
      showAlert('เพิ่มไม่สำเร็จ');
    }
  };

  const handleToggleTimeTracking = async (checked) => {
    if (!(await confirmAsync(checked ? 'เปิดการลงเวลา?' : 'ปิดการลงเวลา?'))) return;
    await dispatch(actions.projectPut(id, { time_tracking_enabled: checked }));
    setTimeTrackingEnabled(checked);
    dispatch(actions.projectGet(id));
  };

  const handleToggleTimeTrackingLink = async (checked) => {
    if (!(await confirmAsync(checked ? 'เปิดลิงก์ Manpower?' : 'ปิดลิงก์ Manpower?')))
      return;
    await dispatch(
      actions.projectPut(id, { time_tracking_link_enabled: checked }),
    );
    setTimeTrackingLinkEnabled(checked);
    dispatch(actions.projectGet(id));
  };

  const p = cachedProject || project;
  const link = `${info?.url}/manpower/timestamp/${id}`;

  if (!cachedProject && (project?.isLoading || !project?.isCompleted)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* ── แท็บโฟลเดอร์ 2 แผ่น: แดชบอร์ดโปรเจกต์ / รายละเอียด P111 เดิม ── */}
      <Box sx={{ position: 'relative' }}>
        {recenterPopup && (
          <Tooltip title="ย้าย popup กลับตำแหน่งเดิม" placement="bottom">
            <IconButton
              size="small"
              onClick={recenterPopup}
              sx={{
                position: 'absolute',
                left: '50%',
                top: 4,
                transform: 'translateX(-50%)',
                zIndex: 3,
              }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 2, pt: 1.5 }}>
        {[
          { key: 0, label: L('แดชบอร์ด', 'Dashboard') },
          { key: 1, label: L('รายละเอียด', 'Details') },
        ].map((s) => {
          const active = sheet === s.key;
          return (
            <Box
              key={s.key}
              onClick={() => setSheet(s.key)}
              sx={{
                cursor: 'pointer',
                px: 2.5,
                py: 0.9,
                borderRadius: '10px 10px 0 0',
                fontSize: '0.85rem',
                fontWeight: active ? 700 : 500,
                bgcolor: active ? 'background.paper' : '#e2e8f0',
                color: active ? 'primary.main' : 'text.secondary',
                border: '1px solid',
                borderColor: active ? 'divider' : 'transparent',
                borderBottom: active ? '1px solid transparent' : '1px solid',
                borderBottomColor: active ? 'background.paper' : 'divider',
                mb: '-1px',
                zIndex: active ? 2 : 1,
                position: 'relative',
                transition: 'all 0.12s',
                '&:hover': { bgcolor: active ? 'background.paper' : '#cbd5e1' },
              }}
            >
              {s.label}
            </Box>
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip
          title={L(
            'เปลี่ยนภาษา Popup T-1 ถึง T-7',
            'Switch Popup language',
          )}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPopupLang((prev) => (prev === 'en' ? 'th' : 'en'))}
            sx={{ minWidth: 0, px: 1, py: 0.2, mb: 0.5, fontSize: '0.65rem' }}
          >
            {popupLang === 'en' ? '🇹🇭 ไทย' : '🇬🇧 EN'}
          </Button>
        </Tooltip>
        {sheet === 0 && p?.opportunity_id && (
          <Tooltip title="ดูโอกาสทางธุรกิจต้นทาง" placement="left">
            <IconButton size="small" onClick={() => setShowOpportunity(true)} sx={{ mb: 0.5 }}>
              <BusinessCenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="ประวัติการแก้ไขโครงการนี้" placement="left">
          <IconButton size="small" onClick={() => setHistoryOpen(true)} sx={{ mb: 0.5 }}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="โหมดทดสอบ — ลองปรับข้อมูลจำลองเล่น ไม่กระทบข้อมูลจริง" placement="left">
          <IconButton size="small" onClick={() => setSandboxOpen(true)} sx={{ mb: 0.5 }}>
            <ScienceIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onPopOut && (
          <Tooltip title={L('แยกไปเปิดเป็นหน้าต่างใหม่', 'Open in new window')} placement="left">
            <IconButton size="small" onClick={onPopOut} sx={{ mb: 0.5 }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onPopIn && (
          <Tooltip title="กลับไปเป็น popup ในแท็บเดิม" placement="left">
            <IconButton size="small" onClick={onPopIn} sx={{ mb: 0.5, ml: 0.5 }}>
              <CallReceivedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      </Box>
      <Divider />

      {p?.opportunity_id && (
        <Dialog open={showOpportunity} onClose={() => setShowOpportunity(false)} maxWidth="lg" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            <OpportunityDetail propId={p.opportunity_id} onClose={() => setShowOpportunity(false)} />
          </DialogContent>
        </Dialog>
      )}

      <DevTabBadge tabNum={1} name="แดชบอร์ด (sheet)" active={sheet === 0} />
      <DevTabBadge tabNum={2} name="รายละเอียด (sheet)" active={sheet === 1} />

      {/* Expense Detail — ย้ายมาไว้นอก {sheet===1} เพราะแท็บแดชบอร์ด (sheet 0, row 9 "รายการ
          ตั้งเบิกที่รอจ่าย") ก็เปิด dialog นี้ได้เหมือนกัน ต้องเห็นไม่ว่าจะอยู่ sheet ไหน */}
      <DevWinBadge
        num={113}
        name="Project · Expense Detail"
        open={!!expenseDialogId}
      />
      <Dialog
        open={!!expenseDialogId}
        onClose={() => setExpenseDialogId(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
        <DialogContent sx={{ p: 2 }}>
          {expenseDialogId && (
            <ExpensesDetail
              propId={expenseDialogId}
              onClose={() => {
                setExpenseDialogId(null);
                dispatch(actions.budgetAll({ project_id: id }));
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Items — เช่นเดียวกับ Expense Detail ด้านบน ต้องเห็นไม่ว่าจะอยู่ sheet ไหน เพราะ
          แท็บแดชบอร์ด (sheet 0, row 13 "รอเงินคืน" breakdown) ก็เปิด dialog นี้ได้เหมือนกัน */}
      <RefundItemsModal
        open={!!refundModalBudgetId}
        budget={(p?.budget || []).find(
          (b) => String(b?._id || b?.id) === refundModalBudgetId,
        )}
        expenses={(p?.expenses || []).filter(
          (e) => String(e?.budget) === refundModalBudgetId,
        )}
        onClose={() => setRefundModalBudgetId(null)}
        onUpdated={() => {
          dispatch(actions.projectGet(id));
          dispatch(actions.budgetAll({ project_id: id }));
        }}
      />

      {/* Pending Items (AP) — เช่นเดียวกับด้านบน ต้องเห็นไม่ว่าจะอยู่ sheet ไหน เพราะแท็บ
          แดชบอร์ด (sheet 0, row 10 "AP PENDING ตามงบย่อย" breakdown) ก็เปิด dialog นี้ได้เหมือนกัน */}
      <PendingItemsModal
        open={!!pendingModalBudgetId}
        budget={(p?.budget || []).find(
          (b) => String(b?._id || b?.id) === pendingModalBudgetId,
        )}
        onClose={() => setPendingModalBudgetId(null)}
        onUpdated={() => dispatch(actions.projectGet(id))}
      />

      <ProjectHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        projectId={id}
        projectName={p?.name}
        uiLang={popupLang}
      />

      <ProjectSandboxDialog
        open={sandboxOpen}
        onClose={() => setSandboxOpen(false)}
        sourceProject={p}
        uiLang={popupLang}
      />

      {sheet === 0 && (
        <Box sx={{ p: 2.5 }}>
          <ProjectSingleDashboard
            project={p}
            projectId={id}
            uiLang={popupLang}
            onLocalUpdate={(patch) =>
              setCachedProject((prev) => ({ ...prev, ...patch }))
            }
            onSaved={onSaved}
            onPopIn={onPopIn}
            onPrev={onPrev}
            onNext={onNext}
            navIndex={navIndex}
            navTotal={navTotal}
            filterChips={filterChips}
            onOpenExpenseDetail={(expenseId) => setExpenseDialogId(expenseId)}
            onOpenRefundModal={(budgetId) => setRefundModalBudgetId(budgetId)}
            onOpenPendingModal={(budgetId) => setPendingModalBudgetId(budgetId)}
          />
        </Box>
      )}

      {sheet === 1 && (
      <Box sx={{ p: 2.5, overflowX: 'hidden' }}>
      {/* ── Header ── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ width: 200, flexShrink: 0 }}>
            <Box
              sx={{
                bgcolor: '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: 1,
                px: 2,
                py: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="#1e3a8a">
                {p?.project_number || '-'}
              </Typography>
            </Box>
            {p?.oldest_job_date && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 0.3 }}
              >
                Mo-{String(new Date(p.oldest_job_date).getMonth() + 1).padStart(2, '0')} / Yr-{String(new Date(p.oldest_job_date).getFullYear() % 100).padStart(2, '0')}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: '1 1 280px', minWidth: 260, maxWidth: 420, overflow: 'hidden' }}>
            <TruncatedTooltipText text={p?.customer} variant="body1" fontWeight={700} />
            <TruncatedTooltipText text={p?.name} variant="body2" color="#2563eb" />
          </Box>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {p?.deliver_status?.status && (
            <Chip
              size="small"
              label={L('ส่งมอบแล้ว', 'Delivered')}
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          <Tooltip title={L('แก้ไขโครงการ', 'Edit project')}>
            <IconButton size="small" onClick={() => setOpenEdit(true)}>
              <ModeEditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOpenDeliver(true)}
            sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
          >
            {L('ส่งมอบงาน', 'Deliver')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            onClick={() => setOpenTimeTracking(true)}
            sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
          >
            {L('ลงเวลา', 'Time')} {timeTrackingEnabled ? L('(เปิด)', '(On)') : L('(ปิด)', '(Off)')}
          </Button>
        </Stack>
      </Stack>

      {/* Edit Popup */}
      <ProjectEditPopup
        propId={id}
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSaved={() => {
          dispatch(actions.projectGet(id));
          onSaved?.();
        }}
      />

      <Divider sx={{ mb: 1 }} />

      <DevTabBadge tabNum={3} name="แดชบอร์ด (tab)" active={tab === 0} bottom={48} />
      <DevTabBadge tabNum={4} name="งบประมาณ (tab)" active={tab === 1} bottom={48} />
      <DevTabBadge tabNum={5} name="รายการเบิก (tab)" active={tab === 2} bottom={48} />
      <DevTabBadge tabNum={6} name="ลงเวลา (tab)" active={tab === 3} bottom={48} />
      <DevTabBadge tabNum={7} name="เอกสาร (tab)" active={tab === 4} bottom={48} />

      {/* ── Tabs ── */}
      <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, fontSize: '0.8rem', py: 0.5 },
          }}
        >
          <Tab label={L('แดชบอร์ด', 'Dashboard')} />
          <Tab label={L('งบประมาณ', 'Budget')} />
          <Tab label={L('รายการเบิก', 'Expenses')} />
          <Tab label={L('ลงเวลา', 'Time')} />
          <Tab label={L('เอกสาร', 'Documents')} />
        </Tabs>
        {/* ปุ่มเลื่อนก่อนหน้า/ถัดไป — วางกึ่งกลางแถวแท็บ ไม่ขึ้นกับความกว้างฝั่งซ้าย/ขวา */}
        {(onPrev || onNext) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <Tooltip title={onPrev ? L('โปรเจกต์ก่อนหน้า', 'Previous project') : L('ไม่มีโปรเจกต์ก่อนหน้า', 'No previous project')} placement="top">
              <Box component="span">
                <IconButton size="small" onClick={onPrev} disabled={!onPrev} sx={{ opacity: onPrev ? 1 : 0.35 }}>
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
            {navIndex >= 0 && navTotal > 0 && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {navIndex + 1} OF {navTotal}
              </Typography>
            )}
            <Tooltip title={onNext ? L('โปรเจกต์ถัดไป', 'Next project') : L('ไม่มีโปรเจกต์ถัดไป', 'No next project')} placement="top">
              <Box component="span">
                <IconButton size="small" onClick={onNext} disabled={!onNext} sx={{ opacity: onNext ? 1 : 0.35 }}>
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {tab === 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => budgetTableRef.current?.toggleAllRows()}
            startIcon={
              budgetToggleState.allExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
            }
            disabled={budgetToggleState.disabled}
          >
            {budgetToggleState.allExpanded
              ? L('ปิดทั้งหมด', 'Collapse all')
              : L('ดูค่าใช้จ่ายทั้งหมด', 'View all expenses')}
          </Button>
        )}
      </Stack>

      <Box sx={{ pt: 1.5 }}>
        {/* แดชบอร์ด */}
        {tab === 0 && (
          <BudgetTable
            ref={budgetTableRef}
            budget={budget?.rows}
            handleEditBudget={handleEditBudget}
            handleDeleteBudget={handleDeleteBudget}
            onEditExpense={handleEditExpense}
            hideToggleButton
            onToggleStateChange={setBudgetToggleState}
            onRefundUpdated={() => dispatch(actions.budgetAll({ project_id: id }))}
            uiLang={popupLang}
          />
        )}

        {/* งบประมาณ */}
        {tab === 1 && (
          <Box>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ mb: 1 }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={() => setOpenBudget(true)}
              >
                {L('เพิ่มงบประมาณ', 'Add budget')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<UploadFileIcon />}
                onClick={() => setCsvUploadOpen(true)}
              >
                {L('นำเข้าข้อมูล', 'Import')}
              </Button>
            </Stack>
            <BudgetListTable
              budget={budget?.rows}
              handleEditBudget={handleEditBudget}
              handleDeleteBudget={handleDeleteBudget}
              uiLang={popupLang}
            />
          </Box>
        )}

        {/* รายการเบิก */}
        {tab === 2 && (
          <Box>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ mb: 1 }}
            >
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<PictureAsPdfIcon />}
                disabled={expensesLoading || expensesList.length === 0}
                onClick={handleExportExpensesPdf}
              >
                {L('ส่งออก PDF', 'Export PDF')}
              </Button>
            </Stack>
            {expensesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : expensesList.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                {L('ยังไม่มีรายการเบิก', 'No expenses')}
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        L('วันที่', 'Date'),
                        L('รหัส', 'Code'),
                        L('รายการ', 'Item'),
                        L('หมวด', 'Category'),
                        L('ผู้เบิก', 'Requester'),
                        L('ผู้รับเงิน', 'Payee'),
                        L('จำนวน', 'Amount'),
                        L('สถานะ', 'Status'),
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            bgcolor: '#3730a3',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            whiteSpace: 'nowrap',
                            py: 0.75,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expensesList.map((exp, i) => {
                      const st =
                        EXP_STATUS_MAP[exp.status] || EXP_STATUS_MAP.PENDING;
                      const empName = exp.employee
                        ? formatEmployeeThaiName(exp.employee)
                        : '-';
                      return (
                        <TableRow
                          key={exp._id || i}
                          onClick={() => setExpenseDialogId(exp._id)}
                          sx={{
                            bgcolor: i % 2 === 0 ? '#fff' : '#f8f8fc',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#eff6ff' },
                          }}
                        >
                          <TableCell
                            sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                          >
                            {exp.date
                              ? dayjs(exp.date).format('DD/MM/BB')
                              : '-'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              whiteSpace: 'nowrap',
                              color: '#64748b',
                            }}
                          >
                            {exp.code || '-'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Tooltip
                              title={exp.name || ''}
                              placement="top-start"
                              disableHoverListener={!exp.name}
                            >
                              <span>{exp.name || '-'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              maxWidth: 130,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: '#64748b',
                            }}
                          >
                            <Tooltip
                              title={exp.budget?.name || ''}
                              placement="top-start"
                              disableHoverListener={!exp.budget?.name}
                            >
                              <span>{exp.budget?.name || '-'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                          >
                            {empName}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              whiteSpace: 'nowrap',
                              color: '#64748b',
                            }}
                          >
                            {exp.payee?.name || '-'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              whiteSpace: 'nowrap',
                              textAlign: 'right',
                              fontWeight: 600,
                            }}
                          >
                            {Number(exp.price || 0).toLocaleString('th-TH', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell sx={{ py: 0.4 }}>
                            <Chip
                              label={L(st.label, {
                                PENDING: 'Pending',
                                PREAPPROVE: 'Reapproval',
                                PREPARE: 'Preparing',
                                APPROVE: 'Approved',
                                SUCCESS: 'Completed',
                                REJECT: 'Rejected',
                                CANCEL: 'Cancelled',
                              }[exp.status] || st.label)}
                              size="small"
                              sx={{
                                bgcolor: st.bg,
                                color: st.color,
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                height: 18,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ลงเวลา */}
        {tab === 3 && <CheckInlist timestamp={timestamp} uiLang={popupLang} />}

        {/* เอกสาร */}
        {tab === 4 && (
          <Box>
            {/* ลิงก์ Google Drive จาก LINE OA — แยกจาก documents[] ด้านล่างชัดเจนตามที่ user ขอ
            (แอปนอกเขียนผ่าน backend/routes/external.js เท่านั้น ดู
            backend/services/external.service.js — อ่านอย่างเดียวในนี้ ไม่มีปุ่มแก้ไข/ลบ) */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  {L('ลิงก์ Google Drive จาก LINE OA (แก้ไข/ลบเองไม่ได้)', 'Google Drive link from LINE OA (read-only)')}
                </Typography>
              </Stack>
              {p?.google_drive_url ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <OpenInNewIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                  <Link
                    href={p.google_drive_url}
                    target="_blank"
                    rel="noopener"
                    sx={{
                      fontSize: '0.8rem',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.google_drive_url}
                  </Link>
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  {L('ยังไม่มีลิงก์จาก LINE OA', 'No link from LINE OA yet')}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {L('เอกสารแนบ', 'Attached documents')}
            </Typography>
            {p?.documents?.length > 0 ? (
              <Stack spacing={0.5}>
                {p.documents.map((doc, i) => (
                  <Box
                    key={i}
                    onClick={() => window.open(doc.url, '_blank')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <OpenInNewIcon
                      fontSize="small"
                      color="primary"
                      sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.title || `${L('เอกสาร', 'Document')} ${i + 1}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.url}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                {L(
                  'ยังไม่มีเอกสาร — กดปุ่มแก้ไข (✏️) เพื่อเพิ่มลิงค์',
                  'No documents — click Edit (✏️) to add a link',
                )}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* ── Modals ── */}
      <DevWinBadge num={114} name="Project · Add Budget" open={openBudget} />
      <Modal open={openBudget} onClose={() => setOpenBudget(false)}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              เพิ่มงบประมาณ
            </Typography>
            {nextBudgetNo && (
              <Tooltip
                title="เลขนี้เป็นค่าประมาณ อาจเปลี่ยนได้ถ้ามีการสร้างงบฯ พร้อมกัน"
                arrow
              >
                <Chip
                  label={nextBudgetNo}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                />
              </Tooltip>
            )}
          </Box>
          <form onSubmit={handleSubmit(onSubmitBudget)}>
            <BudgetForm Controller={Controller} control={control} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" type="submit" size="small">
                บันทึก
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <DevWinBadge
        num={115}
        name="Project · Edit Budget"
        open={openEditBudget}
      />
      <Modal open={openEditBudget} onClose={() => setOpenEditBudget(false)}>
        <Box sx={modalStyle}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            แก้ไขงบประมาณ
          </Typography>
          <form onSubmit={handleSubmit(onSubmitEditBudget)}>
            <BudgetForm Controller={Controller} control={control} showNumber />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" type="submit" size="small">
                บันทึก
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <DevWinBadge num={116} name="Project · Deliver" open={openDeliver} />
      <Modal open={openDeliver} onClose={() => setOpenDeliver(false)}>
        <Box sx={modalStyle}>
          <form onSubmit={handleSubmit(handleDeliver)}>
            <DeliverForm
              dateDeliver={dateDeliver}
              setDateDeliver={setDateDeliver}
              title="ส่งมอบงาน"
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" type="submit" size="small">
                บันทึก
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <DevWinBadge
        num={117}
        name="Project · Time Tracking"
        open={openTimeTracking}
      />
      <Modal open={openTimeTracking} onClose={() => setOpenTimeTracking(false)}>
        <Box sx={{ ...modalStyle, width: 440 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            จัดการลงเวลา
          </Typography>
          {!timeTrackingEnabled && (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
              ปิดการลงเวลาผ่าน Profile
            </Alert>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={timeTrackingEnabled}
                onChange={(e) => handleToggleTimeTracking(e.target.checked)}
                color="success"
                size="small"
              />
            }
            label="ลงเวลาผ่านหน้า Profile"
          />
          <Divider sx={{ my: 1.5 }} />
          <FormControlLabel
            control={
              <Switch
                checked={timeTrackingLinkEnabled}
                onChange={(e) => handleToggleTimeTrackingLink(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="ลงเวลาผ่านลิงก์ Manpower"
          />
          {timeTrackingLinkEnabled && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <TextField
                value={link}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard.writeText(link)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button size="small" onClick={() => setOpenTimeTracking(false)}>
              ปิด
            </Button>
          </Box>
        </Box>
      </Modal>

      <CSVUploadModal
        csvData={csvData}
        setCsvData={setCsvData}
        fileTemplateURL="/filetemplate/budget_template.csv"
        handleAddFromFile={handleAddFromFile}
        handleClose={() => {
          setCsvUploadOpen(false);
          setCsvData([]);
        }}
        isOpen={csvUploadOpen}
        titleName="รายการงบประมาณจากไฟล์ CSV"
        header={0}
        anotherComponent={<div />}
      />

      </Box>
      )}
    </Box>
  );
}
